import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealDetail = () => {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/deals/${id}`);
        setDeal(r.data);
      } catch (e) {
        setError('Deal not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleClick = () => {
    fetch(`${API}/deals/${id}/click`, { method: 'POST' }).catch(() => {});
  };

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-16 text-zinc-400">Loading…</div>;
  if (error || !deal) return (
    <div className="max-w-4xl mx-auto px-6 py-16 text-center">
      <h1 className="text-2xl font-bold">Deal not found</h1>
      <Link to="/" className="mt-4 inline-block text-amber-400 hover:underline">← Back home</Link>
    </div>
  );

  const hasComparison = deal.original_price && deal.sale_price && deal.original_price > deal.sale_price;
  const STORE_LABEL = {
    amazon: 'Amazon', walmart: 'Walmart', target: 'Target',
    bestbuy: 'Best Buy', ebay: 'eBay', costco: 'Costco',
    samsclub: "Sam's Club", homedepot: 'Home Depot',
    lowes: "Lowe's", other: 'the retailer',
  };
  const storeLabel = STORE_LABEL[deal.store] || deal.store;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="text-sm text-zinc-400 mb-6 flex flex-wrap gap-2">
        <Link to="/" className="hover:text-amber-400">Home</Link>
        <span>/</span>
        {deal.store !== 'other' && (
          <>
            <Link to={`/deals/store/${deal.store}`} className="hover:text-amber-400">{storeLabel}</Link>
            <span>/</span>
          </>
        )}
        <Link to={`/deals/category/${deal.category}`} className="hover:text-amber-400 capitalize">{deal.category}</Link>
      </nav>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden aspect-[4/3] flex items-center justify-center">
          {deal.image_url ? (
            <img
              src={deal.image_url}
              alt={deal.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display='flex'; }}
            />
          ) : null}
          <div
            className="w-full h-full bg-gradient-to-br from-amber-500/20 via-zinc-900 to-zinc-950 flex-col items-center justify-center text-center px-8"
            style={{ display: deal.image_url ? 'none' : 'flex' }}
          >
            <div className="text-xs uppercase tracking-[0.3em] text-amber-300/80 font-semibold">{storeLabel}</div>
            <div className="mt-3 text-5xl font-extrabold text-white">
              {deal.discount_percentage ? `${deal.discount_percentage}%` : 'Hot'}
            </div>
            <div className="mt-1 text-sm text-zinc-400">off retail</div>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold">{storeLabel}</span>
            <span className="text-xs text-zinc-500">·</span>
            <span className="text-xs uppercase tracking-wider text-zinc-400">{deal.category}</span>
          </div>
          <h1 data-testid="deal-detail-title" className="text-3xl font-extrabold leading-tight">{deal.title}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            {deal.sale_price && (
              <span className="text-4xl font-extrabold text-white">${deal.sale_price.toFixed(2)}</span>
            )}
            {hasComparison && (
              <span className="text-xl text-zinc-500 line-through">${deal.original_price.toFixed(2)}</span>
            )}
            {deal.discount_percentage > 0 && (
              <span className="px-2 py-1 rounded bg-amber-400 text-black text-sm font-bold">-{deal.discount_percentage}%</span>
            )}
          </div>

          {deal.ai_summary && (
            <div className="mt-5 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-1">Why this is a good deal</div>
              <p className="text-zinc-200">{deal.ai_summary}</p>
            </div>
          )}

          {deal.description && (
            <div className="mt-4 text-zinc-300 leading-relaxed whitespace-pre-wrap">{deal.description}</div>
          )}

          <a
            href={deal.affiliate_url || deal.url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={handleClick}
            data-testid="deal-detail-cta"
            className="mt-6 inline-flex items-center justify-center rounded-md bg-amber-400 hover:bg-amber-300 text-black font-semibold px-6 py-3"
          >
            Get this deal at {storeLabel} →
          </a>

          <div className="mt-4 text-xs text-zinc-500">
            Prices and stock subject to change. DealPing may earn a commission if you purchase through our link.
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealDetail;
